import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import {
  GridCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { isActionRole } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import { ServiceState } from '@src/store/reducers/serviceData';
import storeConnector from '@src/store/storeConnector';
import { Actions } from '@src/types/Actions';
import { Device } from '@src/types/Device';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import isCommandsEnabled from '@src/utils/isCommandsEnabled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeviceDataTable from '../DeviceDataTable/DeviceDataTable';
import UIBox from '../UIBox';
import ModesDataCell from './ModesDataCell';

interface UnitListProps {
  siteMeta: SiteMeta;
  actions: Actions;
  isControlsInterLocked: ServiceState['isControlsInterLocked'];
  role: UserRole;
}

function UnitList({
  siteMeta,
  actions,
  isControlsInterLocked,
  role,
}: UnitListProps) {
  const {
    data: widgetConfig,
    isLoading,
    isError,
    isSuccess,
  } = useGetInternalWidgetConfig<WidgetTypeDeviceList>('unitList');

  const [selectedUnit, setSelectedUnit] =
    useState<GridRowSelectionModel | null>(null);
  const hasInitializedRef = useRef(false);

  // Set initial selected unit when data loads successfully
  const firstDevice = useMemo(
    () => widgetConfig?.devices?.[0],
    [widgetConfig?.devices],
  );
  const firstDeviceEngId = firstDevice?.engineeringId;

  useEffect(() => {
    if (
      isSuccess &&
      firstDevice &&
      firstDeviceEngId &&
      !hasInitializedRef.current
    ) {
      setSelectedUnit([firstDeviceEngId]);
      actions.setActiveUnit(firstDevice);
      hasInitializedRef.current = true;
    }
  }, [isSuccess, firstDevice, firstDeviceEngId, actions]);

  const dataPoints = useMemo(
    () => widgetConfig?.dataPoints ?? [],
    [widgetConfig?.dataPoints],
  );

  const deviceMap = useMemo(
    () =>
      new Map<GridRowId, Device>(
        widgetConfig?.devices?.map((d) => [d.engineeringId, d]) ?? [],
      ),
    [widgetConfig?.devices],
  );

  const handleRowClick = ({ id }: GridRowParams): void => {
    const newActiveUnit = deviceMap.get(id);
    if (newActiveUnit) {
      actions.setActiveUnit(newActiveUnit);
      setSelectedUnit([id as string]);
    }
  };

  const renderCustomColumn = useCallback(
    (params: GridCellParams) => (
      <ModesDataCell
        unitSN={params.row.id as string}
        siteMeta={siteMeta}
        row={params.row}
      />
    ),
    [siteMeta],
  );

  const handleOpenCmdModal = useCallback(
    (deviceId: string) =>
      !isControlsInterLocked &&
      actions.showCommandsPopup(true, {
        popupType: 'unit',
        sn: deviceId,
      }),
    [isControlsInterLocked, actions],
  );

  return (
    <UIBox header='Unit List' isError={isError}>
      <DeviceDataTable
        widgetId='unitList'
        enableNotesColumn={siteMeta.ui.Notes_Enable}
        enableCmdBtnColumn={isCommandsEnabled()}
        disableCmdBtn={!isActionRole(role)}
        disableCmdBtnByInterlock={isControlsInterLocked}
        hideFooter
        disableColumnMenu
        rowSelectionModel={selectedUnit ?? ['']}
        onRowClick={handleRowClick}
        devices={widgetConfig?.devices}
        dataPoints={dataPoints}
        pointConfigRefs={widgetConfig?.pointConfigRefs || []}
        getRowClassName={() => 'MuiDataGrid-clickable'}
        loading={isLoading}
        additionalClassnames='unit-list-data-table'
        useDualListView={widgetConfig?.useDualListView ?? false}
        openCmdModal={handleOpenCmdModal}
        disableVirtualization
        customColumn={renderCustomColumn}
      />
    </UIBox>
  );
}

export default storeConnector(UnitList, {
  config: ['siteMeta'],
  user: ['role'],
  service: ['isControlsInterLocked'],
});
